import type { AppCard, AppCardImage, AppCardStatus, AppMessageView } from '../api/client';
import { messageBlocks, trimLink } from './richtext';

/**
 * Messages from webhooks (1.12.19): Stitch's own Markdown, read for showing, and the helpers that
 * lay out their cards. The server turns Slack's mrkdwn into this Markdown
 * (apps/api/src/webhooks/mrkdwn.ts); Stitch-format senders write it themselves:
 *
 *   **bold**  _italic_ or *italic*  ~~struck~~  `code`  ``` blocks ```  [label](https://…)
 *   bare https:// links   "> " quotes   # headings (shown bold)   \* escapes
 *
 * Plain data in, plain data out: nothing here is ever HTML, and links are only http(s) or mailto.
 */

export type MdNode =
  | { t: 'text'; text: string }
  | { t: 'code'; code: string }
  | { t: 'strong' | 'em' | 'del'; children: MdNode[] }
  | { t: 'link'; url: string; children: MdNode[] };

export type MdBlock = { t: 'p' | 'quote' | 'h'; nodes: MdNode[] } | { t: 'code'; code: string; lang: string | null };

/** Characters a backslash makes plain. */
const ESCAPABLE = new Set(['\\', '`', '*', '_', '~', '[', ']', '(', ')', '#', '>', '!', '-']);

/** An address that may be a link: http(s) or mailto, nothing else. */
export function safeUrl(raw: string): string | null {
  const url = raw.trim();
  if (/^mailto:[^\s<>"@]+@[^\s<>"]+$/i.test(url)) return url;
  if (!/^https?:\/\/[^\s<>"]+$/i.test(url)) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'http:' || u.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

const isWord = (c: string | undefined) => Boolean(c && /[\p{L}\p{N}]/u.test(c));
const isSpace = (c: string | undefined) => c === undefined || /\s/.test(c);

/** Where the closing `mark` is, from `from` on (escapes and code skipped; never past a line's end), or -1. */
function closing(s: string, from: number, mark: string): number {
  for (let k = from; k < s.length; k++) {
    const c = s[k]!;
    if (c === '\n') return -1;
    if (c === '\\') {
      k++;
      continue;
    }
    if (c === '`') {
      const end = s.indexOf('`', k + 1);
      const nl = s.indexOf('\n', k + 1);
      if (end > k && (nl < 0 || end < nl)) {
        k = end;
        continue;
      }
    }
    if (s.startsWith(mark, k) && !isSpace(s[k - 1]) && k > from) {
      // A single * or _ isn't half of a ** or __.
      if (mark.length === 1 && (s[k + 1] === mark || s[k - 1] === mark)) continue;
      if (mark.length === 1 && isWord(s[k + 1])) continue;
      return k;
    }
  }
  return -1;
}

/** One paragraph's words: emphasis, code, links. */
export function markdownInline(s: string): MdNode[] {
  const out: MdNode[] = [];
  let buf = '';
  const flush = () => {
    if (buf) out.push({ t: 'text', text: buf });
    buf = '';
  };
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    if (c === '\\' && ESCAPABLE.has(s[i + 1] ?? '')) {
      buf += s[i + 1];
      i += 2;
      continue;
    }
    if (c === '`') {
      const end = s.indexOf('`', i + 1);
      const nl = s.indexOf('\n', i + 1);
      if (end > i + 1 && (nl < 0 || end < nl)) {
        flush();
        out.push({ t: 'code', code: s.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }
    if (c === '[') {
      // [label](address): the label up to its ], then the address with no spaces.
      let j = i + 1;
      while (j < s.length && s[j] !== ']' && s[j] !== '\n') j += s[j] === '\\' ? 2 : 1;
      if (s[j] === ']' && s[j + 1] === '(') {
        const end = s.indexOf(')', j + 2);
        const url = end > j + 2 ? safeUrl(s.slice(j + 2, end)) : null;
        if (url) {
          flush();
          out.push({ t: 'link', url, children: markdownInline(s.slice(i + 1, j)) });
          i = end + 1;
          continue;
        }
      }
    }
    if ((c === 'h' || c === 'H') && /^https?:\/\//i.test(s.slice(i, i + 8)) && !isWord(s[i - 1])) {
      const m = /^https?:\/\/[^\s<>"`]+/i.exec(s.slice(i));
      const url = m ? trimLink(m[0]) : '';
      const ok = url ? safeUrl(url) : null;
      if (ok) {
        flush();
        out.push({ t: 'link', url: ok, children: [{ t: 'text', text: url }] });
        i += url.length;
        continue;
      }
    }
    const two = s.slice(i, i + 2);
    if ((two === '**' || two === '~~' || two === '__') && !isSpace(s[i + 2])) {
      const end = closing(s, i + 2, two);
      if (end > i + 2) {
        flush();
        out.push({ t: two === '~~' ? 'del' : 'strong', children: markdownInline(s.slice(i + 2, end)) });
        i = end + 2;
        continue;
      }
    }
    if ((c === '*' || c === '_') && s[i + 1] !== c && !isSpace(s[i + 1]) && !isWord(s[i - 1])) {
      const end = closing(s, i + 1, c);
      if (end > i + 1) {
        flush();
        out.push({ t: 'em', children: markdownInline(s.slice(i + 1, end)) });
        i = end + 1;
        continue;
      }
    }
    buf += c;
    i++;
  }
  flush();
  return out;
}

/** Text as blocks: code (``` fences), quotes ("> "), headings ("# "), and paragraphs. */
export function markdownBlocks(text: string): MdBlock[] {
  const out: MdBlock[] = [];
  for (const b of messageBlocks(text.replace(/\r\n?/g, '\n'))) {
    if (b.kind === 'code') {
      out.push({ t: 'code', code: b.code, lang: b.lang });
      continue;
    }
    let para: string[] = [];
    let quote: string[] = [];
    const flush = () => {
      if (para.length) {
        const words = para.join('\n').replace(/^\n+|\n+$/g, '');
        if (words) out.push({ t: 'p', nodes: markdownInline(words) });
      }
      if (quote.length) out.push({ t: 'quote', nodes: markdownInline(quote.join('\n')) });
      para = [];
      quote = [];
    };
    for (const line of b.text.split('\n')) {
      const q = /^\s{0,3}>\s?(.*)$/.exec(line);
      const h = /^\s{0,3}#{1,6}\s+(.*)$/.exec(line);
      if (q) {
        if (para.length) {
          const keep = quote;
          quote = [];
          flush();
          quote = keep;
        }
        quote.push(q[1]!);
      } else if (h) {
        flush();
        out.push({ t: 'h', nodes: markdownInline(h[1]!) });
      } else {
        if (quote.length) {
          const keep = para;
          para = [];
          flush();
          para = keep;
        }
        para.push(line);
      }
    }
    flush();
  }
  return out;
}

/** Plain words, as a notification would say them. */
export function markdownPlain(text: string): string {
  const words = (nodes: MdNode[]): string => nodes.map((n) => (n.t === 'text' ? n.text : n.t === 'code' ? n.code : words(n.children))).join('');
  return markdownBlocks(text)
    .map((b) => (b.t === 'code' ? b.code : words(b.nodes)))
    .join('\n')
    .trim();
}

/** The word said with a card's colour (no colour-only meaning). */
export function statusWord(s: AppCardStatus | null): string | null {
  return s === 'ok' ? 'OK' : s === 'info' ? 'Info' : s === 'warning' ? 'Warning' : s === 'critical' ? 'Critical' : null;
}

/** Fields in rows: short ones two to a row, long ones on their own. */
export function fieldRows<T extends { short: boolean }>(fields: T[]): T[][] {
  const rows: T[][] = [];
  for (const f of fields) {
    const last = rows[rows.length - 1];
    if (f.short && last && last.length === 1 && last[0]!.short) last.push(f);
    else rows.push([f]);
  }
  return rows;
}

/** A picture in a webhook's message: through Stitch once fetched; while it's coming, a placeholder; else a link to where it was. */
export type ShownImage = { kind: 'picture'; n: number; version: string } | { kind: 'pending' } | { kind: 'link'; url: string; label: string };

export function shownImage(app: Pick<AppMessageView, 'images'>, ref: AppCardImage): ShownImage {
  const img = ref.image === null ? undefined : app.images.find((x) => x.n === ref.image);
  if (img?.status === 'ok' && img.version) return { kind: 'picture', n: img.n, version: img.version };
  if (img?.status === 'pending') return { kind: 'pending' };
  let label = ref.url;
  try {
    const u = new URL(ref.url);
    label = `${u.host}${u.pathname.length > 1 ? u.pathname : ''}`;
  } catch {
    label = ref.url;
  }
  return { kind: 'link', url: safeUrl(ref.url) ?? '', label: label.length > 60 ? `${label.slice(0, 59)}…` : label };
}

/** A card's colour for its bar: the sender's, when it's a colour (else the status's own, else none). */
export function barColor(card: Pick<AppCard, 'color' | 'status'>): string | null {
  return card.color && /^#[0-9a-f]{6}$/i.test(card.color) ? card.color : null;
}

/** What a screen reader hears for a card before its contents: "Critical: Elevated API errors". */
export function cardLabel(card: Pick<AppCard, 'status' | 'title' | 'author'>): string {
  const word = statusWord(card.status);
  const title = card.title ? markdownPlain(card.title.text) : card.author ? markdownPlain(card.author.name) : 'Card';
  return word ? `${word}: ${title}` : title;
}
